import { getChilds } from "@base/data/Products";
import { type Product } from "@base/types/product.types";
// import FlexibleImage from "@components/common/FlexibleImage";
import { CartContext } from "@components/contexts/CartContext";
import Layout from "@components/layouts/Layout";
import HeroSection from "@components/newVersion/HeroSection";
import addToBasketSfx from "@static/sounds/addProduct.mp3";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { FaTruckFast } from "react-icons/fa6";
import { MdAddShoppingCart } from "react-icons/md";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";
import useSWR from "swr";
import useSound from "use-sound";
import { cn } from "../../../utils/cn";
import {
    CLOUDINARY_PRESETS,
    getCloudinaryUrl,
} from "../../newVersion/CloudinaryImage";
import Loading from "./Loading";

// import Seo from "@components/common/Seo";
// import Product from "@base/templates/tienda/all";
// import Emoji from "../../../utils/emoji";

// Fix para que funciona ReactJson
const ReactJsonView =
    typeof window !== `undefined` ? require("react-json-view").default : null;

const Component = ({
    item,
    title,
    children: contentData,
}: {
    item: Product;
    title: string;
    children: React.ReactNode;
}) => {
    const [product, setProduct] = useState<Product>(item);

    const { data, isLoading, isValidating } = useSWR(
        "/.netlify/functions/products",
        axios.get,
        {
            dedupingInterval: 10 * 1000,
        },
    );

    const loading = isLoading || isValidating;

    useEffect(() => {
        if (data) {
            // @ts-expect-error - El producto se actualiza con los datos más recientes, por si han cambiado
            const singleItem = data?.data?.products?.find(
                (x: any) => x.id === item.id,
            );

            // @ts-expect-error - Obtenemos los datos de los productos hijos, por si han cambiado
            const childs = data?.data?.products?.filter(
                (childItem: any) =>
                    singleItem.childs &&
                    singleItem.childs.includes(childItem.id),
            );

            // Actualizamos los datos del producto, por si se hubieran actualizado
            if (singleItem) {
                setProduct({
                    ...singleItem,
                    childs,
                });
            }
        }
    }, [data, item.id]);

    // Obtenemos la imagen del producto para mostrarla en el SEO
    const cloudinaryImage = getCloudinaryUrl(getAndParseImage(product), {
        ...CLOUDINARY_PRESETS.productSeo,
    });

    // Si hay imagen principal, modo static; si no, modo fixed (para mostrar landscape/portrait)
    const mode = product.hero ? "fixed" : "static";

    // Estado para detectar orientación
    return (
        <Layout
            hideBackToTop={false}
            hideFooter={false}
            hideHeader={false}
            mode={mode}
            seo={{
                title: product.title,
                description: product.description,
                image: cloudinaryImage,
            }}
        >
            <RenderHeroSection product={product} />

            <section className={styles.section}>
                <Loading loading={loading} />
                <RenderItemInfo
                    item={product}
                    data={contentData}
                    title={title}
                    // mode={mode}
                />
            </section>
        </Layout>
    );
};

const RenderItemInfo = ({
    item,
    data,
    title,
    // mode = "static",
}: {
    item: Product;
    data: React.ReactNode;
    title?: string;
    // mode?: "static" | "fixed";
}) => {
    const childs = getChilds(item.id);
    const showEvolmindAlert =
        item.evolmind || childs.some((child) => child.evolmind);

    return (
        <>
            <div className="space-y-20">
                {/** Cabecera */}
                <div className="space-y-5">
                    <h1 className="text-balance text-center text-4xl font-extrabold leading-none tracking-tight text-gray-700 md:text-5xl lg:text-6xl">
                        {item.title}
                    </h1>

                    {item.description && (
                        <h2 className="mb-6 text-center text-lg font-normal text-gray-600 sm:px-16 lg:text-xl xl:px-48">
                            {item.description}
                        </h2>
                    )}
                </div>

                {/** Imagen flexible */}
                {/*!item.productImage?.landscape &&
                    !item.productImage?.portrait && (
                        <FlexibleImage
                            src={`/images/products${item.images[0]}`}
                            alt={item.title}
                            className="h-auto w-full rounded-xl"
                        />
                    )*/}

                {/*item.productImage?.landscape && (
                    <FlexibleImage
                        src={`/images/products${item.productImage.landscape}`}
                        alt={item.title}
                        className="hidden h-auto w-full rounded-xl md:block"
                    />
                )*/}

                {/*item.productImage?.portrait && (
                    <FlexibleImage
                        src={`/images/products${item.productImage.portrait}`}
                        alt={item.title}
                        className="h-auto w-full rounded-lg md:hidden"
                    />
                )*/}

                {/** Si hay stock */}
                {item.stock ? (
                    <>
                        {/** Precios */}
                        {Array.isArray(item.childs) &&
                        item.childs.length > 0 ? (
                            <PricesSection item={item} />
                        ) : (
                            !item.special_buy && <PriceSection item={item} />
                        )}
                    </>
                ) : (
                    <div className="flex items-center justify-center gap-2 rounded-full bg-purple-200 px-5 py-2 text-sm text-purple-800">
                        {item.stock_message || "Sin estoc en este momento."}
                    </div>
                )}

                {/** Descripción y contenido */}
                <MoreInfo title={title}>{data}</MoreInfo>

                {/** Si es un libro avisamos */}
                {item.type === "book" && item.can_be_sent && (
                    <div className="border-b-2 border-t-2 border-gray-300 p-5 text-center text-sm text-gray-600">
                        <FaTruckFast className="mr-2 inline-block h-6 w-6" />
                        <span className="font-bold text-gray-600">
                            Envío en 3–4 días hábiles
                        </span>
                        <br />
                        <span className="text-gray-600">
                            Entrega estimada según disponibilidad y logística.
                        </span>
                    </div>
                )}

                <HomologatedSection item={item as Product} />

                {/** Añadimos aviso evolmind */}
                {showEvolmindAlert && (
                    <div className="mx-auto !mt-10 max-w-3xl rounded-md border-l-2 border-r-2 border-red-200 bg-red-100 px-5 py-3 text-sm text-red-600">
                        <strong>Aviso:</strong> El material del Campus Online no
                        es descargable.
                    </div>
                )}

                {/* Avisamos si el producto estará disponible hasta la fecha del examen */}
                {item.availableUntilExam && (
                    <div className="mx-auto !mt-10 max-w-3xl rounded-md border-l-2 border-r-2 border-yellow-200 bg-yellow-100 px-5 py-3 text-sm text-yellow-600">
                        <strong>Aviso:</strong> Este producto estará disponible
                        hasta el día del primer examen oficial de la
                        convocatoria para la que se ha creado este curso. Una
                        vez celebrado el primer examen de esta convocatoria, el
                        acceso se cerrará automáticamente.
                    </div>
                )}
            </div>

            <div className="text-right text-xs text-gray-500">
                REF: {item.id}
            </div>

            {process.env.NODE_ENV === "development" && (
                <ReactJsonView src={item} collapsed={true} />
            )}
        </>
    );
};

const getImage = (item: any) => {
    // Obtenemos la imagen por defecto del producto
    const images = item.images || [];

    // Si no hay imágenes, usamos una por defecto
    const defaultImage = images.length > 0 ? images[0] : "/products/common/0";

    return defaultImage;
};

const PricesSection = ({ item }: { item: any }) => {
    return (
        <section className="py-5">
            <div className="space-y-2">
                <h2 className="text-center text-base font-semibold leading-7 text-cpurple">
                    Modalidades disponibles
                </h2>

                <p className="text-center text-4xl font-bold tracking-tight text-gray-700 sm:text-5xl">
                    Opciones para todo tipo de opositores
                </p>

                {item.multiple_languages && (
                    <p className="mx-auto max-w-2xl text-center text-lg leading-8 text-gray-600">
                        Además, te ofrecemos la posibilidad de realizar el curso
                        en el <strong>idioma que prefieras</strong>. Disponemos
                        de
                        <strong>varias opciones lingüísticas</strong> para que
                        puedas estudiar con comodidad y sacar el máximo provecho
                        al contenido. Solo tienes que{" "}
                        <strong>elegir el idioma</strong> que mejor se adapte a
                        ti al iniciar el curso.
                    </p>
                )}

                {item.multiple_plans && (
                    <p className="mx-auto max-w-2xl text-center text-lg leading-8 text-gray-600">
                        Elige el plan que mejor se adapte a tus necesidades y
                        presupuesto. Cada plan superior incluye
                        <strong>más beneficios</strong> y supone un{" "}
                        <strong>ahorro considerable</strong> a largo plazo,
                        facilitando tu preparación para la oposición de forma
                        más completa y eficiente.
                    </p>
                )}
            </div>

            <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-5 md:grid-cols-2 lg:gap-10">
                {item?.childs?.map((child: any, index: number) => (
                    <PriceSectionItem key={index} item={child} />
                ))}
            </div>
        </section>
    );
};

const PriceSectionItem = ({ item }: { item: any }) => {
    const [play] = useSound(addToBasketSfx, { volume: 0.25 });
    const { addProduct } = useContext(CartContext);

    if (!item.stock) return null;

    const units = item.items || 1;
    const pricePerUnit = item.price / units;

    const price = item.price;
    const defaultInParent = item.default_in_parent;

    const formatter = new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const handleAddToCart = () => {
        // @ts-expect-error - Añadimos el producto al carrito usando el contexto
        addProduct(item);

        // Reproducimos el sonido de añadir al carrito
        play();

        // Añadimos el item al carrito
        toast.success("Producto añadido al carrito", {
            position: "bottom-center",
            closeOnClick: true,
            pauseOnHover: false,
            autoClose: 3000,
        });
    };

    return (
        <div
            className={cn(
                "relative space-y-4 rounded-2xl border-2 p-8 pb-5 duration-300 hover:border-ccyan",
                defaultInParent && "border-cpurple",
            )}
        >
            {defaultInParent && (
                <div className="absolute right-0 top-0 rounded-bl-lg rounded-tr-lg bg-cpurple px-3">
                    <span className="mt-6 text-xs font-medium uppercase text-white">
                        Más popular
                    </span>
                </div>
            )}

            <h1
                className={cn(
                    "text-balance font-bold text-gray-600",
                    defaultInParent && "text-cpurple",
                )}
            >
                {item.title}
            </h1>

            {item.description && (
                <h2 className="text-sm text-gray-700">{item.description}</h2>
            )}

            <div>
                <span className="text-4xl font-bold text-gray-600">
                    {formatter.format(pricePerUnit)}
                </span>
                {/*<span className="ml-2 text-gray-700 text-semibold text-sm">/ sesión</span>*/}
            </div>

            <button
                onClick={handleAddToCart}
                disabled={!item.stock}
                className={
                    cn(
                        "flex w-full flex-row items-center justify-center gap-2 rounded-lg border px-5 py-3 text-sm duration-300",
                        defaultInParent
                            ? "border-cpurple bg-cpurple text-white duration-300 hover:bg-white hover:text-cpurple"
                            : "border-gray-400 text-gray-600 hover:border-gray-600",
                        !item.stock && "pointer-events-none opacity-25",
                    )
                    // styles.button,
                    // "text-cpurple duration-300 border-cpurple hover:bg-cpurple text-sm",
                    // !item.stock && "opacity-25 pointer-events-none",
                }
                // onClick={handleAddToCart}
            >
                <MdAddShoppingCart className="h-6 w-6" />
                <span className="hidden font-medium md:inline">
                    Pagas{" "}
                    <span className="font-bold">{formatter.format(price)}</span>{" "}
                    {units > 1 && (
                        <>
                            {" "}
                            por <span className="font-bold">{units}</span>{" "}
                            unidades
                        </>
                    )}
                </span>

                <span className="font-medium md:hidden">
                    Pagas{" "}
                    <span className="font-bold">{formatter.format(price)}</span>
                </span>
            </button>

            <div className="mt-2 text-left text-[12px] uppercase text-ccyan">
                Ref: {item.id}
            </div>
        </div>
    );
};

const PriceSection = ({ item }: { item: any }) => {
    const [play] = useSound(addToBasketSfx, { volume: 0.25 });
    const { addProduct } = useContext(CartContext);

    const offer = item.offer;
    const currentPrice = item.price;
    // const offerText = offer && item.offer_text;

    // const priceText = item.price_text || item.split_payment;
    const isOffer = offer && offer !== currentPrice && currentPrice < offer;

    /*const isBook = item.type === "book";
    const isApp = item?.app;
    const units = item?.items || 1;
    const lowStock = item.lowStock;*/

    const formatter = new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const handleAddToCart = () => {
        // @ts-expect-error - Añadimos el producto al carrito usando el contexto
        addProduct(item);

        // Reproducimos el sonido de añadir al carrito
        play();

        // Añadimos el item al carrito
        toast.success("Producto añadido al carrito", {
            position: "bottom-center",
            closeOnClick: true,
            pauseOnHover: false,
            autoClose: 3000,
        });
    };

    return (
        <section className="mx-auto max-w-xl space-y-5">
            <div
                className={cn(
                    "relative space-y-4 rounded-2xl border-2 border-cpurple p-8 duration-300",
                )}
            >
                <h1 className={cn("text-balance font-bold text-cpurple")}>
                    {item.title}
                </h1>

                {item.description && (
                    <h2 className="text-sm text-gray-700">
                        {item.description}
                    </h2>
                )}

                {!isOffer ? (
                    <div className="text-center">
                        <span className="text-4xl font-bold text-gray-800">
                            {formatter.format(currentPrice)}
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-row items-baseline justify-center gap-2">
                        <span className="text-2xl font-bold text-gray-500 line-through">
                            {formatter.format(offer)}
                        </span>

                        <span className="text-4xl font-bold text-gray-800">
                            {formatter.format(currentPrice)}
                        </span>
                    </div>
                )}

                <button
                    onClick={handleAddToCart}
                    disabled={!item.stock}
                    className={cn(styles.buyButton)}
                >
                    <MdAddShoppingCart className="h-6 w-6" />
                    <span className="text-lg font-medium">
                        Pagar{" "}
                        <span className="font-bold">
                            {formatter.format(currentPrice)}
                        </span>
                    </span>
                </button>
            </div>
        </section>
    );
};

const MoreInfo = ({
    children,
    title,
}: {
    children?: React.ReactNode;
    title?: string;
}) => {
    if (!children) {
        return <></>;
    }

    return (
        <section className="mx-auto max-w-4xl text-gray-700">
            <div className="space-y-2">
                <h2 className="text-center text-base font-semibold leading-7 text-cpurple">
                    Más información
                </h2>

                <hr className="!my-10 mx-auto h-[2px] w-48 rounded-sm border-0 bg-ccyan lg:!my-14" />

                {title && (
                    <p className="text-center text-4xl font-bold tracking-tight text-gray-700 sm:text-5xl">
                        {title}
                    </p>
                )}
            </div>

            <div className="mx-auto mt-10 space-y-5 text-center leading-8 text-gray-600">
                {children}
            </div>
        </section>
    );
};

const HomologatedSection = ({ item }: { item: Product }) => {
    if (!item.homologated || item.homologated?.length === 0) {
        return null;
    }

    return (
        <section className="mx-auto !mt-10 max-w-xl rounded-lg border-l-4 border-orange-400 bg-orange-50/50 p-5 text-left font-inter text-gray-500 lg:max-w-2xl xl:max-w-3xl">
            <h2 className={cn(styles.title)}>
                <FaShieldAlt className="mr-2 inline-block size-5 text-orange-500" />{" "}
                Certificación
            </h2>

            {item.homologated?.map((homologation, index) => (
                <div key={index}>
                    {homologation.description && (
                        <div className="mt-5">
                            <ReactMarkdown>
                                {homologation.description ?? "-"}
                            </ReactMarkdown>
                        </div>
                    )}

                    {homologation.instructions && (
                        <div className="mt-5">
                            <ReactMarkdown>
                                {homologation.instructions}
                            </ReactMarkdown>
                        </div>
                    )}

                    <ul className={cn(styles.list, "mt-5")}>
                        <li>
                            <strong>Homologado por:</strong> {homologation.from}
                        </li>
                        <li>
                            <strong>Año formativo:</strong> {homologation.year}
                        </li>
                        <li>
                            <strong>Número de homologación:</strong>{" "}
                            {homologation.code}
                        </li>
                        <li>
                            <strong>Horas:</strong>{" "}
                            {homologation.durationInHours} horas
                        </li>
                    </ul>

                    {homologation.document && (
                        <div className="mt-5 flex flex-col gap-2 rounded border border-orange-200 bg-orange-100 p-4 text-sm text-orange-900">
                            <div className="mb-1">
                                <ReactMarkdown>
                                    {homologation.document.info}
                                </ReactMarkdown>
                            </div>

                            <a
                                href={homologation.document.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block w-fit rounded bg-orange-400 px-4 py-2 font-semibold text-white shadow transition-colors hover:bg-orange-500"
                            >
                                Ver documento adjunto
                            </a>
                        </div>
                    )}
                </div>
            ))}
        </section>
    );
};

// Renderiza el HeroSection según el modo y las imágenes disponibles
const RenderHeroSection = ({ product }: { product: Product }) => {
    if (!product.hero) {
        return null;
    }

    // Usar directamente las rutas de product.image para cada orientación
    const landscapeImage = product.hero.landscape
        ? getCloudinaryUrl(
              getAndParseImage({
                  ...product,
                  images: [product.hero.landscape],
              }),
              CLOUDINARY_PRESETS.heroLandscape,
          )
        : undefined;

    const portraitImage = product.hero.portrait
        ? getCloudinaryUrl(
              getAndParseImage({
                  ...product,
                  images: [product.hero.portrait],
              }),
              CLOUDINARY_PRESETS.heroPortrait,
          )
        : undefined;

    return (
        <>
            <HeroSection
                src={landscapeImage}
                firstTitle={product.hero.firstTitle}
                secondTitle={product.hero.secondTitle}
                thirdTitle={product.hero.thirdTitle}
                showScrollDownIcon={true}
                className="hidden w-full md:block md:h-screen"
            />

            <HeroSection
                src={portraitImage}
                showScrollDownIcon={true}
                firstTitle={product.hero.firstTitle}
                secondTitle={product.hero.secondTitle}
                thirdTitle={product.hero.thirdTitle}
                className="h-screen md:hidden"
            />
        </>
    );
};

const getAndParseImage = (product: Product) => {
    let image = getImage(product);

    // Miramos si la ruta no empieza por "products/" o "/products/", la añadimos
    // en caso contrario
    if (!image.startsWith("products/") && !image.startsWith("/products/")) {
        const parsedImage = `products${image}`;
        return parsedImage;
    }

    // Eliminamos la primera "/" si existe
    if (image.startsWith("/")) {
        image = image.substring(1);
    }

    return image;
};

const styles = {
    section: "container lg:px-0 py-10 space-y-10",
    buyButton:
        "border text-sm rounded-lg w-full py-3 px-5 flex flex-row items-center justify-center gap-2 text-white duration-300 border-cpurple bg-cpurple hover:bg-white hover:text-cpurple",
    strong: "font-semibold text-ccyan",
    title: "font-bold text-xl font-montserrat",
    list: "list-disc ml-4 space-y-2",
};
export default Component;
